import { useCallback, useState } from "react";

/**
 * Which rows of a list have a write in flight.
 *
 * A set, not a `number | null`. Holding only the most recent id re-enables the
 * first row's button the moment a second row is clicked -- reopening the exact
 * double-submit the guard exists to stop -- and whichever request settles first
 * clears the marker for both. A framework with a synchronous rerun lock would
 * give this for free; React has no equivalent, so every per-row write goes
 * through here.
 */
export function useBusyRows<Id = number>() {
  const [busy, setBusy] = useState<ReadonlySet<Id>>(new Set());

  const isBusy = useCallback((id: Id) => busy.has(id), [busy]);

  /** Marks `id` busy until `work` settles, however it settles. */
  const run = useCallback((id: Id, work: Promise<unknown>) => {
    setBusy((b) => new Set(b).add(id));
    return work.finally(() =>
      setBusy((b) => {
        const next = new Set(b);
        next.delete(id);
        return next;
      }),
    );
  }, []);

  return { isBusy, run };
}
